import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ExtendedProductDetailProduct, FinishData } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { finishUpchargeCents } from 'shared/types/utils/finishUpcharge';
import { CART_ITEM_FINISH_QUERY_PARAM, FINISH_QUERY_PARAM, SHEEN_QUERY_PARAM } from 'helpers/constants/query-params';
import { useSetQueryParam } from 'helpers/hooks/useSetQueryParam';
import { normalize } from 'helpers/utils/variantOrdering';

const UNFINISHED = 'Unfinished';
const STANDARD_BLACK = 'Standard Black';
const DEFAULT_SHEEN = 'Clearcoat 5 Sheen';

const isUnfinished = (finish: FinishData | undefined) => finish?.name === UNFINISHED;
const getFinishFromParam = (
  finishes: ExtendedProductDetailProduct['finishes'] | undefined,
  finishParam: string | null,
) => {
  if (!finishParam || !finishes) return undefined;
  return finishes[finishParam];
};
/**
 * The finish a PDP opens on when the shopper has not chosen one.
 *
 * Wood opens on Unfinished and metal on Standard Black — the base configuration in each family.
 * Without this the opening selection fell out of the finish map's own key order, which is
 * alphabetical, so a tread opened on Basalt and a post on Alabaster White.
 *
 * Returns the `[key, finish]` pair rather than either half: the selection and the query-param key
 * are stored separately, and they have to come from the same entry to stay in step.
 */
const getDefaultFinishEntry = (
  finishes: ExtendedProductDetailProduct['finishes'] | undefined,
  isWoodProduct?: boolean,
): [string, FinishData] | undefined => {
  const entries = finishes ? Object.entries(finishes) : [];
  if (!entries.length) return undefined;

  const preferred = isWoodProduct ? UNFINISHED : STANDARD_BLACK;
  // Falling back to the first entry keeps a product that offers neither opening on something.
  return entries.find(([, finish]) => normalize(finish.name) === normalize(preferred)) ?? entries[0];
};

const getFinishFromCartParam = (
  finishes: ExtendedProductDetailProduct['finishes'] | undefined,
  cartFinishParam: string | null,
) => {
  if (!cartFinishParam || !finishes) return undefined;
  return Object.values(finishes).find((finish) => finish.name === cartFinishParam);
};

/**
 * Returns the finish + sheen options and selection state for the **currently selected variant**.
 *
 * When `variant` is provided the hook resolves the variant's finish/sheen set from the deduped
 * `product.finishSets` / `product.sheenSets` via `product.variantFinishSetId[sku]` /
 * `product.variantSheenSetId[sku]`, falling back to the flat legacy `product.finishes` /
 * `product.sheens` so products without per-variant data continue to work identically. Finish
 * `price` is materialized here for the current variant (`variant.finishPrice × multiplier`),
 * since the shared sets are group-level and carry no price.
 *
 * On a variant change:
 * - If the previously-selected finish exists on the new variant → keep it (price updates automatically).
 * - If not → auto-select the first available finish and update URL params.
 */
export const useFinishAndSheens = (product: ExtendedProductDetailProduct, variant?: ExtendedVariant) => {
  const searchParams = useSearchParams();
  const { setQueryParam, removeQueryParam } = useSetQueryParam();

  // Resolve the variant's deduped finish set, then materialize per-variant price over it.
  const currentVariantFinishes = useMemo<ExtendedProductDetailProduct['finishes']>(() => {
    const setId = variant?.sku ? product.variantFinishSetId?.[variant.sku] : undefined;
    const finishSet = setId ? product.finishSets?.[setId] : undefined;
    const source = finishSet && Object.keys(finishSet).length > 0 ? finishSet : product.finishes;
    if (!source) return undefined;

    const finishPriceCents = variant?.finishPrice?.centAmount;
    const materialized: NonNullable<ExtendedProductDetailProduct['finishes']> = {};
    for (const [finishKey, finishData] of Object.entries(source)) {
      materialized[finishKey] = {
        ...finishData,
        // Whole cents: downstream code adds this straight onto the base price, so a fractional
        // cent here would surface as a total we never actually charge.
        price:
          finishPriceCents != null
            ? finishUpchargeCents(finishPriceCents, finishData.name, finishData.multiplier)
            : finishData.price,
      };
    }
    return materialized;
  }, [product.finishSets, product.variantFinishSetId, product.finishes, variant?.sku, variant?.finishPrice?.centAmount]);

  const currentVariantSheens = useMemo<string[] | undefined>(() => {
    const setId = variant?.sku ? product.variantSheenSetId?.[variant.sku] : undefined;
    const sheenSet = setId ? product.sheenSets?.[setId] : undefined;
    if (sheenSet && sheenSet.length > 0) return sheenSet;
    return product.sheens;
  }, [product.sheenSets, product.variantSheenSetId, product.sheens, variant?.sku]);

  const finishOptions = useMemo(
    () => (currentVariantFinishes ? Object.values(currentVariantFinishes) : undefined),
    [currentVariantFinishes],
  );

  const sheensOptions = currentVariantSheens;

  const [finish, setFinish] = useState<FinishData | undefined>(() => {
    const finishParam = searchParams.get(FINISH_QUERY_PARAM);
    const cartFinishParam = searchParams.get(CART_ITEM_FINISH_QUERY_PARAM);
    const existingFinish =
      getFinishFromParam(currentVariantFinishes, finishParam) ??
      getFinishFromCartParam(currentVariantFinishes, cartFinishParam);
    if (existingFinish) return existingFinish;
    return getDefaultFinishEntry(currentVariantFinishes, product.isWoodProduct)?.[1];
  });
  const [finishKey, setFinishKey] = useState<string | undefined>(() => {
    const finishParam = searchParams.get(FINISH_QUERY_PARAM);
    if (finishParam && currentVariantFinishes?.[finishParam]) return finishParam;
    return getDefaultFinishEntry(currentVariantFinishes, product.isWoodProduct)?.[0];
  });

  const [sheen, setSheen] = useState<string | undefined>(() => {
    const initialFinishParam = searchParams.get(FINISH_QUERY_PARAM);
    const initialCartFinishParam = searchParams.get(CART_ITEM_FINISH_QUERY_PARAM);
    const initialFinish =
      getFinishFromParam(currentVariantFinishes, initialFinishParam) ??
      getFinishFromCartParam(currentVariantFinishes, initialCartFinishParam) ??
      getDefaultFinishEntry(currentVariantFinishes, product.isWoodProduct)?.[1];
    if (isUnfinished(initialFinish)) return undefined;

    const sheenParam = searchParams.get(SHEEN_QUERY_PARAM);
    if (sheenParam) {
      const existingSheen = sheensOptions?.find((s) => s === sheenParam) ?? undefined;
      if (existingSheen) return existingSheen;
    }
    if (sheensOptions?.includes(DEFAULT_SHEEN)) return DEFAULT_SHEEN;
    return sheensOptions?.[0];
  });

  // Clean up stale sheen query param on mount if finish is Unfinished
  useEffect(() => {
    if (isUnfinished(finish) && searchParams.has(SHEEN_QUERY_PARAM)) {
      removeQueryParam(SHEEN_QUERY_PARAM);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track previous SKU so we can react only when the variant actually changes
  const prevSkuRef = useRef<string | undefined>(variant?.sku);

  useEffect(() => {
    const currentSku = variant?.sku;
    if (!currentSku || currentSku === prevSkuRef.current) return;
    prevSkuRef.current = currentSku;

    const variantFinishMap = currentVariantFinishes;
    const variantSheenList = currentVariantSheens;
    const availableFinishValues = variantFinishMap ? Object.values(variantFinishMap) : undefined;

    if (finish && variantFinishMap) {
      // Try to find the same finish key (exact match) on the new variant
      const sameFinishKey = Object.entries(variantFinishMap).find(
        ([, fd]) => fd.name === finish.name,
      );
      if (sameFinishKey) {
        // Finish exists — update with new variant's pricing
        const [finishKey, updatedFinishData] = sameFinishKey;
        setFinish(updatedFinishData);
        setFinishKey(finishKey);
        setQueryParam(FINISH_QUERY_PARAM, finishKey);
        removeQueryParam(CART_ITEM_FINISH_QUERY_PARAM);
      } else {
        // Finish unavailable on the new variant — fall back to that family's default
        const defaultEntry = getDefaultFinishEntry(variantFinishMap, product.isWoodProduct);
        if (defaultEntry) {
          const [defaultFinishKey, defaultFinish] = defaultEntry;
          setFinish(defaultFinish);
          setFinishKey(defaultFinishKey);
          setQueryParam(FINISH_QUERY_PARAM, defaultFinishKey);
          removeQueryParam(CART_ITEM_FINISH_QUERY_PARAM);
        } else {
          setFinish(undefined);
          setFinishKey(undefined);
          removeQueryParam(FINISH_QUERY_PARAM);
        }
      }
    } else if (!finish && availableFinishValues?.length) {
      // No finish was selected but the new variant has finishes — take that family's default
      const defaultEntry = getDefaultFinishEntry(variantFinishMap, product.isWoodProduct);
      if (defaultEntry) {
        const [defaultFinishKey, defaultFinish] = defaultEntry;
        setFinish(defaultFinish);
        setFinishKey(defaultFinishKey);
        setQueryParam(FINISH_QUERY_PARAM, defaultFinishKey);
        removeQueryParam(CART_ITEM_FINISH_QUERY_PARAM);
      }
    }

    const newFinishAfterChange = (() => {
      if (!variantFinishMap) return finish;
      const sameFinish = finish
        ? Object.values(variantFinishMap).find((fd) => fd.name === finish.name)
        : undefined;
      return sameFinish ?? getDefaultFinishEntry(variantFinishMap, product.isWoodProduct)?.[1];
    })();

    if (isUnfinished(newFinishAfterChange)) {
      setSheen(undefined);
      removeQueryParam(SHEEN_QUERY_PARAM);
    } else if (variantSheenList?.length) {
      const currentSheenStillValid = sheen && variantSheenList.includes(sheen);
      if (!currentSheenStillValid) {
        const defaultSheen = variantSheenList.includes(DEFAULT_SHEEN) ? DEFAULT_SHEEN : variantSheenList[0];
        setSheen(defaultSheen);
        setQueryParam(SHEEN_QUERY_PARAM, defaultSheen);
      }
    } else {
      setSheen(undefined);
      removeQueryParam(SHEEN_QUERY_PARAM);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant?.sku]);

  const onChangeFinish = useCallback(
    (finishLabel: string) => {
      if (!currentVariantFinishes) return;
      const foundFinish = Object.entries(currentVariantFinishes).find(
        ([_finishKey, finishData]) => finishData.name === finishLabel,
      );
      if (foundFinish) {
        const wasUnfinished = isUnfinished(finish);

        setFinish(foundFinish[1]);
        setFinishKey(foundFinish[0]);
        setQueryParam(FINISH_QUERY_PARAM, foundFinish[0]);
        removeQueryParam(CART_ITEM_FINISH_QUERY_PARAM);

        if (isUnfinished(foundFinish[1])) {
          setSheen(undefined);
          removeQueryParam(SHEEN_QUERY_PARAM);
        } else if (wasUnfinished) {
          const defaultSheen = sheensOptions?.includes(DEFAULT_SHEEN) ? DEFAULT_SHEEN : sheensOptions?.[0];
          if (defaultSheen) {
            setSheen(defaultSheen);
            setQueryParam(SHEEN_QUERY_PARAM, defaultSheen);
          }
        }
      }
    },
    [currentVariantFinishes, finish, sheensOptions, setQueryParam, removeQueryParam],
  );

  const onChangeSheen = useCallback(
    (sheen: string) => {
      setSheen(sheen);
      setQueryParam(SHEEN_QUERY_PARAM, sheen);
    },
    [setQueryParam],
  );

  const cartFinishParam = searchParams.get(CART_ITEM_FINISH_QUERY_PARAM);
  const hasUnresolvedCartFinish = !!cartFinishParam && !getFinishFromCartParam(currentVariantFinishes, cartFinishParam);

  return {
    finish,
    finishKey,
    hasUnresolvedCartFinish,
    onChangeFinish,
    sheen,
    onChangeSheen,
    finishes: finishOptions,
    sheens: sheensOptions,
  };
};
